/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */

import {isNotEmpty, sanitizeString} from "../../../../../../../Apis/functions";
import {
    ENTRY_AND_EXIT_FORMS,
    MATERIAL_CONTROL_GATE_EVENT_MODEL,
    MATERIAL_CONTROL_GATE_EVENT_TYPE,
    MATERIAL_CONTROL_PENDING_GATE_EVENT_TYPE,
    MOVEMENT_DIRECTION,
    MOVEMENT_DIRECTION_DATE_DEFAULTS,
    PACKAGING_FIELD_BY_TYPE,
    PLANT_MATERIAL_CONTROL_ITEM_MODEL,
    THERMO_TRAILER_MODEL,
    buildEntryAndExitFormItem,
    getMovementDirectionOption,
    getMovementDirectionOptionByValue,
    areValidPlantMaterialControlItems,
    wasMaterialControlDispatched
} from "../../../../../MaterialControl/Model/Model";
import {STATUS_MODEL} from "../../../../../../../enums/statusEnum";
import {TRANSPORT_FIELDS as CRATES_AND_PALLETS_TRANSPORT_FIELDS}
    from "../../../../../MaterialControl/SubComponents/EntryAndExitForms/CratesAndPalletsReturn/CratesAndPalletsTransportSection";
import {TRANSPORT_FIELDS as POULTRY_TRUCK_EXIT_TRANSPORT_FIELDS}
    from "../../../../../MaterialControl/SubComponents/EntryAndExitForms/PoultryTruckExit/PoultryTruckExitTransportSection";
import {TRANSPORT_FIELDS as LIVE_POULTRY_TRUCK_TRANSPORT_FIELDS}
    from "../../../../../MaterialControl/SubComponents/EntryAndExitForms/LivePoultryTruck/LivePoultryTruckTransportSection";

/**
 * Maximum length the notes of the gate log accept.
 */
export const GATE_LOG_NOTES_LENGTH = 512;

/**
 * Movements of the gate that are already written on the folio.
 *
 * The gate registers the return of the vehicle with a movement of its own, so
 * the arrival and the departure are read the same way: the log the guard wrote
 * for them is only read and is no longer completed.
 */
export const MATERIAL_CONTROL_GATE_MOVEMENT_RECORDED_STATUS = [
    STATUS_MODEL.ARRIVAL_RECORDED,
    STATUS_MODEL.DEPARTURE_RECORDED
];

/**
 * Statuses a folio is left in once the gate has nothing else to register on it.
 *
 * The dispatch hands the material over and the movement of the vehicle closes
 * the return the folio declared, so a folio left in one of them is only read:
 * its log displays the movement the guard wrote and the actions that close it
 * are no longer offered.
 */
export const MATERIAL_CONTROL_REGISTERED_GATE_STATUS = [
    STATUS_MODEL.DISPATCHED,
    ...MATERIAL_CONTROL_GATE_MOVEMENT_RECORDED_STATUS
];

/**
 * Tells whether the guard reads the log of a movement of the gate on the folio.
 *
 * The log is written for the movement the folio waits for — the arrival of the
 * vehicle or its departure — and keeps being read once that movement was
 * registered, so every form reads it the same way instead of naming the
 * statuses it displays it in.
 *
 * @param {Object} item - Folio being read.
 *
 * @returns {boolean} Whether the log of the movement of the gate is read.
 */
export const showsGuardGateMovementLog = (item) => Boolean(
    MATERIAL_CONTROL_PENDING_GATE_EVENT_TYPE[item?.dispatchStatusId]
    || MATERIAL_CONTROL_GATE_MOVEMENT_RECORDED_STATUS.includes(item?.dispatchStatusId)
);

/**
 * Movements of the vehicle a folio can ask the gate for.
 */
const VEHICLE_GATE_EVENT_TYPES = [
    MATERIAL_CONTROL_GATE_EVENT_TYPE[STATUS_MODEL.ARRIVAL_RECORDED],
    MATERIAL_CONTROL_GATE_EVENT_TYPE[STATUS_MODEL.DEPARTURE_RECORDED]
];

/**
 * Tells whether the guard reads the record of the trip in the information of the folio.
 *
 * A folio dispatched without requiring the arrival or the departure of the
 * vehicle is closed by the dispatch itself, so the entry or exit of the trip is
 * not repeated in its information. A folio that requires one of those movements
 * keeps reading it.
 *
 * @param {Object} item - Folio being read.
 *
 * @returns {boolean} Whether the record of the trip is read.
 */
export const showsGuardTripRecord = (item) => item?.dispatchStatusId !== STATUS_MODEL.DISPATCHED
    || (item?.gateRequirements || []).some((e) =>
        VEHICLE_GATE_EVENT_TYPES.includes(e.gateEventType) && e.required !== false
    );

/**
 * Tells whether a folio still owes the arrival of the vehicle it asks for.
 *
 * The materials are dispatched once the vehicle arrives, so a folio that still
 * owes it was not dispatching them yet.
 *
 * @param {Object} item - Folio being read.
 *
 * @returns {boolean} Whether the arrival is still expected at the gate.
 */
export const owesGuardArrival = (item) => (item?.gateRequirements || []).some((e) =>
    e?.gateEventType === MATERIAL_CONTROL_GATE_EVENT_TYPE[STATUS_MODEL.ARRIVAL_RECORDED]
    && e?.required !== false
    && !(item?.gateEvents || []).some((g) => g?.type === e.gateEventType)
);

/**
 * Tells whether a folio asks the gate for the arrival of the vehicle.
 *
 * The arrival is the first movement the gate registers, so a folio that asks for
 * it is the one that reads the vehicle when it reaches the plant, whether that
 * arrival was already registered or is still awaited.
 *
 * @param {Object} item - Folio being read.
 *
 * @returns {boolean} Whether the arrival is part of what the folio asks the gate.
 */
export const requiresGuardArrival = (item) => (item?.gateRequirements || []).some((e) =>
    e?.gateEventType === MATERIAL_CONTROL_GATE_EVENT_TYPE[STATUS_MODEL.ARRIVAL_RECORDED]
    && e?.required !== false
);

/**
 * Tells whether the guard captures the transport of the folio.
 *
 * The vehicle is read in the moment it is in front of the guard: a folio that
 * waits for the arrival of the unit reads it there, and one that goes straight
 * to its dispatch reads it while that dispatch is awaited. Once that moment
 * passed the transport is no longer part of the log, because what was written
 * on it is read in the information of the folio.
 *
 * @param {Object} item - Folio being read.
 *
 * @returns {boolean} Whether the transport is captured at the gate.
 */
export const showsGuardTransport = (item) => item?.dispatchStatusId === STATUS_MODEL.PENDING_ARRIVAL
    || (item?.dispatchStatusId === STATUS_MODEL.PENDING_TO_DISPATCH && !requiresGuardArrival(item));

/**
 * Statuses a folio is read in once the gate already wrote the vehicle.
 *
 * The transport is captured in a single moment, so every step that comes after
 * it reads the unit the movement travels with: the arrival the gate registered,
 * the dispatch it waits for, the material already handed over, the departure it
 * owes and the one it left with. A folio reported with discrepancies is read the
 * same way, because the report is reviewed against the vehicle that came.
 */
const GUARD_TRANSPORT_RECORD_STATUS = [
    STATUS_MODEL.ARRIVAL_RECORDED,
    STATUS_MODEL.PENDING_TO_DISPATCH,
    STATUS_MODEL.DISPATCHED,
    STATUS_MODEL.PENDING_DEPARTURE,
    STATUS_MODEL.DEPARTURE_RECORDED,
    STATUS_MODEL.DISCREPANCY
];

/**
 * Tells whether the guard only reads the transport of the folio.
 *
 * The vehicle is written in the moment it is at the gate and is read on every
 * step that follows it, so the guard reads the unit the movement travels with
 * and no longer writes it.
 *
 * @param {Object} item - Folio being read.
 *
 * @returns {boolean} Whether the transport is read without being captured.
 */
export const showsGuardTransportRecord = (item) => !showsGuardTransport(item)
    && GUARD_TRANSPORT_RECORD_STATUS.includes(item?.dispatchStatusId);

/**
 * Tells whether a folio reported with discrepancies was reported while the gate
 * registered a movement of the vehicle.
 *
 * The report replaces the status the folio was waiting in, so the moment it was
 * reported in is read from what the gate already wrote: a folio that still owes
 * the arrival was waiting for the vehicle, and one that already dispatched its
 * materials was waiting for it to leave. Any other one was reported while its
 * dispatch was being completed, so the detail keeps reading that same form,
 * only read, instead of the log of a movement the gate did not reach.
 *
 * @param {Object} item - Folio being read.
 *
 * @returns {boolean} Whether the report was raised on the log of the vehicle.
 */
export const wasGuardReportedOnVehicleMovement = (item) => item?.dispatchStatusId === STATUS_MODEL.DISCREPANCY
    && (owesGuardArrival(item) || wasMaterialControlDispatched(item));

/**
 * Placeholder the notes of the gate log are written with.
 *
 * The guard writes the log of the movement the folio is waiting for, so the
 * vehicle that still has to reach the plant is described as it comes in and
 * the one that still has to leave it as it goes out.
 */
export const GATE_LOG_NOTE_PLACEHOLDER = {
    [STATUS_MODEL.PENDING_ARRIVAL]: 'add_details_about_the_vehicle_condition_seals_or_anything_unusual_upon_arrival',
    [STATUS_MODEL.PENDING_DEPARTURE]: 'add_details_about_the_vehicle_condition_seals_or_anything_unusual_upon_departure',
    [STATUS_MODEL.ARRIVAL_RECORDED]: 'add_details_about_the_vehicle_condition_seals_or_anything_unusual_upon_arrival',
    [STATUS_MODEL.DEPARTURE_RECORDED]: 'add_details_about_the_vehicle_condition_seals_or_anything_unusual_upon_departure'
};

/**
 * Placeholder the notes of the dispatch are written with.
 *
 * The dispatch closes the movement the folio was registered with, so the note
 * asks for what is seen where the material moves: the one that comes into the
 * plant is described as it arrives and the one that leaves it as it goes out.
 */
export const GATE_LOG_DISPATCH_NOTE_PLACEHOLDER = {
    [getMovementDirectionOption(MOVEMENT_DIRECTION.ENTRY).value]:
        GATE_LOG_NOTE_PLACEHOLDER[STATUS_MODEL.PENDING_ARRIVAL],
    [getMovementDirectionOption(MOVEMENT_DIRECTION.EXIT).value]:
        GATE_LOG_NOTE_PLACEHOLDER[STATUS_MODEL.PENDING_DEPARTURE]
};

/**
 * Returns the placeholder the notes of the gate log are written with.
 *
 * @param {Object} params - Movement the note is written for.
 * @param {number} params.statusId - Movement the folio is waiting for.
 * @param {string} params.movementDirection - Direction the folio was registered with.
 *
 * @returns {string|undefined} Placeholder of the movement, undefined when the
 * folio waits for none and the log is read with its default legend.
 */
export const getGateLogNotePlaceholder = ({statusId, movementDirection}) =>
    [STATUS_MODEL.PENDING_TO_DISPATCH, STATUS_MODEL.DISPATCHED].includes(statusId)
        ? GATE_LOG_DISPATCH_NOTE_PLACEHOLDER[movementDirection]
        : GATE_LOG_NOTE_PLACEHOLDER[statusId];

/**
 * Values of a crates and pallets return the requester can leave blank.
 *
 * The transport that returns the crates and the quantities expected at the
 * gate are the ones the guard completes when the vehicle arrives, so the log
 * declares them empty and the fields are read as captured from the first
 * reading of the folio.
 */
export const CRATES_AND_PALLETS_RETURN_GATE_LOG_DEFAULTS = {
    carrierCompany: '',
    tractor: '',
    trailer: '',
    driversName: '',
    sealNumber: '',
    largeBoxes: '',
    smallBoxes: '',
    lids: '',
    pallets: ''
};

/**
 * Values of a poultry truck exit the requester can leave blank.
 *
 * The transport that takes the poultry out of the plant and the thermo
 * trailers it pulls are the ones the guard completes when the vehicle arrives,
 * so the log declares them empty and the fields are read as captured from the
 * first reading of the folio.
 */
export const POULTRY_TRUCK_EXIT_GATE_LOG_DEFAULTS = {
    dollyNo: '',
    licenseNo: '',
    thermoTrailer1: {...THERMO_TRAILER_MODEL},
    thermoTrailer2: {...THERMO_TRAILER_MODEL}
};

/**
 * Thermo trailers of the gate log, in the position the truck pulls them.
 *
 * The service names a trailer by its position, so the log registers the first
 * one it holds as the first the truck pulls and the second one after it.
 */
const THERMO_TRAILER_GATE_FIELDS = ['thermoTrailer1', 'thermoTrailer2'];

/**
 * Name the service reads each field of a thermo trailer with, for the ones the
 * log names differently.
 */
const THERMO_TRAILER_FIELD_NAMES = {
    avisoCzmNo: 'avisoCzm'
};

/**
 * Values of a live poultry truck the requester can leave blank.
 *
 * The transport that carries the live poultry travels with the folio, so at
 * the gate only the license of the driver and the platforms the poultry comes
 * on are read; the log declares them empty and the fields are read as captured
 * from the first reading of the folio.
 */
export const LIVE_POULTRY_TRUCK_GATE_LOG_DEFAULTS = {
    platform1No: '',
    platform2No: '',
    observations: ''
};

/**
 * Name the folio stores each value of the movement with, for the ones the log
 * names differently.
 */
const GATE_MOVEMENT_VALUE_NAMES = {
    observations: 'extraDetails'
};

/**
 * Values of a plant material control the requester can leave blank.
 *
 * The transport of the movement is optional for the requester, so the one that
 * never travelled with the folio is completed at the gate and the log declares
 * it empty until the guard writes it.
 */
export const PLANT_MATERIAL_CONTROL_GATE_LOG_DEFAULTS = {
    carrierCompany: '',
    vehicleInformation: ''
};

/**
 * Values the gate log is opened with when the folio already carries them.
 *
 * Every form leaves different values to the gate, so the log reads back the
 * ones the folio was registered with whatever the type of the form is: those
 * are the ones the guard only reads and the rest are the ones written at the
 * gate. The blocks kept as records of their own — the thermo trailers — are not
 * read here, because each one is completed in the section that captures it.
 */
export const GATE_LOG_REGISTERED_VALUE_FIELDS = Object.entries({
    ...CRATES_AND_PALLETS_RETURN_GATE_LOG_DEFAULTS,
    ...POULTRY_TRUCK_EXIT_GATE_LOG_DEFAULTS,
    ...LIVE_POULTRY_TRUCK_GATE_LOG_DEFAULTS,
    ...PLANT_MATERIAL_CONTROL_GATE_LOG_DEFAULTS
})
    .filter(([, value]) => typeof value !== 'object')
    .map(([name]) => name);

/**
 * Thermo trailers the gate log is opened with.
 *
 * The service stores every trailer as a record of its own and writes it with
 * what the update carries, so the log is opened with the values the folio
 * already registered: those are the ones the guard only reads, and they travel
 * back as they were when the gate completes the rest.
 *
 * @param {Object} item - Folio being read.
 *
 * @returns {Object} Trailers the log is opened with, by the field that holds them.
 */
export const getGateLogRegisteredTrailers = (item) => THERMO_TRAILER_GATE_FIELDS
    .reduce((acc, field) => {
        const trailer = item?.[field];

        if (!trailer)
            return acc;

        acc[field] = Object.keys(THERMO_TRAILER_MODEL).reduce((values, name) => {
            values[name] = trailer[name] ?? '';

            return values;
        }, {});

        return acc;
    }, {});

/**
 * Values of the vehicle a form can leave to the gate.
 *
 * The carrier company and the information of the vehicle travel with every
 * transport, so they are written apart; these are the ones a form captures on
 * its own and the guard completes when the requester leaves them blank.
 *
 * Every value the log holds travels with the update, because a form that does
 * not capture one leaves it blank either way and the folio that already
 * registered it is the one that is read back.
 */
const GATE_VEHICLE_FIELDS = [
    'tractor',
    'trailer',
    'dollyNo',
    'driversName',
    'licenseNo',
    'sealNumber'
];

/**
 * Name the service reads each value of the vehicle with, for the ones the form
 * names differently.
 */
const GATE_VEHICLE_FIELD_NAMES = {
    driversName: 'driverName'
};

/**
 * Returns the values of the transport a form still leaves to the gate.
 *
 * Every field the transport of a form displays is read from the unit that comes
 * to the plant, so the one the requester registered is only read and the one it
 * left blank is written by the guard before the movement is closed.
 *
 * @param {Array} fields - Fields the transport of the form is captured with.
 * @param {Object} item - Folio the movement belongs to.
 *
 * @returns {Array} Names of the values the gate has to write.
 */
const getMissingTransportFields = (fields, item) => fields
    .map((e) => e.name)
    .filter((name) => !isNotEmpty(item?.[name]));

/**
 * Fields of the gate log every type of form is closed with.
 *
 * Each form leaves different values to the gate, so the type of the folio
 * tells which ones the guard has to complete before the movement can be
 * dispatched.
 */
export const GATE_LOG_REQUIRED_FIELDS = {
    // The truck that carries the live poultry is read at the gate, so the values
    // of it the folio left blank are written with the platforms the poultry
    // comes on
    [ENTRY_AND_EXIT_FORMS.LIVE_POULTRY_TRUCK.key]: (item) => [
        ...getMissingTransportFields(LIVE_POULTRY_TRUCK_TRANSPORT_FIELDS, item),
        'platform1No',
        'platform2No'
    ],
    // The transport that returns the crates and the quantities it brings are
    // optional for the requester, so the folio is the one that says which of
    // them belong to the gate: the value it already carries is only read and
    // the one it left blank is the one the guard completes.
    [ENTRY_AND_EXIT_FORMS.CRATES_AND_PALLETS_RETURN.key]: (item) =>
        Object.keys(CRATES_AND_PALLETS_RETURN_GATE_LOG_DEFAULTS)
            .filter((name) => !isNotEmpty(item?.[name])),
    // The transport that takes the poultry out of the plant is read at the gate,
    // so the folio says which of its values belong to the guard: the one it
    // already carries is only read and the one it left blank has to be written
    // before the exit is closed
    [ENTRY_AND_EXIT_FORMS.POULTRY_TRUCK_EXIT.key]: (item) =>
        getMissingTransportFields(POULTRY_TRUCK_EXIT_TRANSPORT_FIELDS, item),
    // The movement of a plant material control happens at the gate, so the date
    // it was registered on is the one the guard completes, along with the
    // transport the requester left blank; a folio that carries no direction is
    // closed as an exit, the same way its log displays it
    [ENTRY_AND_EXIT_FORMS.PLANT_MATERIAL_CONTROL.key]: (item) => [
        (getMovementDirectionOptionByValue(item?.movementDirection)
            || getMovementDirectionOption(MOVEMENT_DIRECTION.EXIT)).dateName,
        ...Object.keys(PLANT_MATERIAL_CONTROL_GATE_LOG_DEFAULTS)
            .filter((name) => !isNotEmpty(item?.[name]))
    ]
};

/**
 * Values of the transport every type of form reads at the gate.
 *
 * The transport is captured while the vehicle is in front of the guard, so its
 * values are asked for by the log that displays it and the rest of the log is
 * asked for by the dispatch that closes the folio.
 */
const GATE_TRANSPORT_FIELDS_BY_FORM = {
    [ENTRY_AND_EXIT_FORMS.CRATES_AND_PALLETS_RETURN.key]:
        CRATES_AND_PALLETS_TRANSPORT_FIELDS.map((e) => e.name),
    [ENTRY_AND_EXIT_FORMS.POULTRY_TRUCK_EXIT.key]:
        POULTRY_TRUCK_EXIT_TRANSPORT_FIELDS.map((e) => e.name),
    [ENTRY_AND_EXIT_FORMS.LIVE_POULTRY_TRUCK.key]:
        LIVE_POULTRY_TRUCK_TRANSPORT_FIELDS.map((e) => e.name),
    [ENTRY_AND_EXIT_FORMS.PLANT_MATERIAL_CONTROL.key]:
        Object.keys(PLANT_MATERIAL_CONTROL_GATE_LOG_DEFAULTS)
};

/**
 * Values the gate has to write before the folio can be closed, for the blocks
 * that are being captured.
 *
 * The transport and the rest of the log are not written in the same moment, so
 * each one is asked for by the block that displays it: the vehicle is asked for
 * while it is at the gate and everything else by the dispatch.
 *
 * @param {Object} [form] - Type of the form being closed.
 * @param {Object} [item] - Folio the movement belongs to.
 * @param {Object} [blocks] - Blocks the values are asked for.
 * @param {boolean} [blocks.transport] - Whether the transport is being captured.
 * @param {boolean} [blocks.dispatch] - Whether the dispatch is being captured.
 *
 * @returns {Array} Names of the values the blocks are closed with.
 */
export const getGateLogRequiredFields = (form, item, {transport = true, dispatch = true} = {}) => {
    const getFields = GATE_LOG_REQUIRED_FIELDS[form?.key];
    const fields = getFields ? getFields(item) : [];
    const transportFields = GATE_TRANSPORT_FIELDS_BY_FORM[form?.key] || [];

    return fields.filter((e) => transportFields.includes(e) ? transport : dispatch);
};

/**
 * Forms whose gate log is written by the dispatch.
 *
 * The dispatch of a plant material control writes no movement of its own: it is
 * the moment the guard completes the log of the folio, so the date of the
 * movement is asked for with it. The materials are optional, because a movement
 * can close without any of them, but the one that was started is not left half
 * written.
 *
 * The crates and pallets return is dispatched the same way: the transport that
 * brings the crates back is the block the guard completes, so it is written
 * with the dispatch that closes the folio.
 *
 * The poultry truck exit and the live poultry truck are closed with the vehicle
 * the guard reads at the gate — the license of the driver, and the platforms
 * the poultry comes on — so their dispatch writes the log the same way.
 */
export const GATE_LOG_DISPATCH_FORMS = [
    ENTRY_AND_EXIT_FORMS.PLANT_MATERIAL_CONTROL.key,
    ENTRY_AND_EXIT_FORMS.CRATES_AND_PALLETS_RETURN.key,
    ENTRY_AND_EXIT_FORMS.POULTRY_TRUCK_EXIT.key,
    ENTRY_AND_EXIT_FORMS.LIVE_POULTRY_TRUCK.key
];

/**
 * Forms whose materials are written in the log of the gate.
 *
 * Only the plant material control lists the materials the movement carries, so
 * it is the one closed with them; the rest of the forms count what they move in
 * blocks of their own and their dispatch is not held by a material.
 */
export const GATE_LOG_MATERIALS_FORMS = [ENTRY_AND_EXIT_FORMS.PLANT_MATERIAL_CONTROL.key];

/**
 * Tells whether a value of the gate log was captured.
 *
 * The log holds the text the guard writes and the dates of the movement, so a
 * text is read as captured when it is not blank and a date when it has a value.
 *
 * @param {*} value - Value captured for the field.
 *
 * @returns {boolean} True when the field carries information.
 */
const isGateLogFieldCaptured = (value) => (
    typeof value === 'string' ? isNotEmpty(value) : value !== null && value !== undefined
);

/**
 * Label every value the gate completes is read with.
 *
 * The movement is not closed while a value is missing, so the guard is told
 * which one it is by the name the field displays and not by the name the model
 * writes it in.
 */
export const GATE_LOG_FIELD_TRANS = {
    notes: 'notes',
    evidence: 'supporting_evidence',
    carrierCompany: 'carrier_company',
    vehicleInformation: 'vehicle_information',
    tractor: 'tractor',
    trailer: 'trailer',
    driversName: 'driver_s_name',
    sealNumber: 'seal_number',
    dollyNo: 'dolly_no',
    licenseNo: 'license_no',
    platform1No: 'platform_1_no',
    platform2No: 'platform_2_no',
    largeBoxes: 'large_boxes',
    smallBoxes: 'small_boxes',
    lids: 'lids',
    pallets: 'pallets',
    entryDate: 'date',
    exitDate: 'date'
};

/**
 * Gate log the guard registers for an entry and exit form.
 *
 * The folio is already registered by the requester, so the guard at the gate
 * only completes what happens when the vehicle arrives: the notes of the
 * movement and the evidence that supports it.
 *
 * A movement that already waits for its dispatch is closed with what the
 * requester left blank, so the date of the direction of the movement and the
 * materials that came in or went out are captured with the same log.
 */
export const GATE_LOG_MODEL = {
    notes: '',
    evidence: [],
    ...MOVEMENT_DIRECTION_DATE_DEFAULTS,
    ...CRATES_AND_PALLETS_RETURN_GATE_LOG_DEFAULTS,
    ...POULTRY_TRUCK_EXIT_GATE_LOG_DEFAULTS,
    ...LIVE_POULTRY_TRUCK_GATE_LOG_DEFAULTS,
    ...PLANT_MATERIAL_CONTROL_GATE_LOG_DEFAULTS,
    items: [buildEntryAndExitFormItem(PLANT_MATERIAL_CONTROL_ITEM_MODEL)],
    /**
     * Tells whether the gate log can be registered.
     *
     * The movement is not closed without the notes of the guard and at least
     * one file of evidence of what entered or left the plant.
     *
     * What the requester left blank is written by the dispatch, so only that
     * log asks for it; the movement the gate registers on its own — the arrival
     * of the vehicle and the departure that takes it out of the plant — is
     * closed with the notes and the evidence of what was seen.
     *
     * @param {Object} params - Values captured outside of the model.
     * @param {Object} params.photoPicker - State of the evidence picker.
     * @param {Object} [params.form] - Type of the form being closed.
     * @param {Object} [params.item] - Folio the movement belongs to.
     * @param {boolean} [params.dispatchLog] - Whether the log being closed is
     * the one the dispatch writes.
     * @param {boolean} [params.transportLog] - Whether the transport is being
     * captured with the log, because the vehicle is read in the moment it is at
     * the gate and not always with the dispatch that closes the folio.
     *
     * @returns {boolean} True when the gate log is complete.
     */
    isValid({photoPicker, form, item, dispatchLog = true, transportLog = dispatchLog}) {
        return isNotEmpty(this.notes)
            && photoPicker?.listFiles?.length > 0
            && this.isValidFormFields(form, item, {transport: transportLog, dispatch: dispatchLog})
            && (!dispatchLog || this.isValidGateMaterials(form));
    },
    /**
     * Tells whether the materials the gate writes are complete.
     *
     * The materials are optional for the form that lists them, so a movement
     * closes with none of them; a material that was started is the one that has
     * to be complete, because the movement it takes is read from it. The forms
     * that write no material at the gate are closed without asking for any,
     * because the guard has nowhere to complete what would be missing.
     *
     * @param {Object} [form] - Type of the form being closed.
     *
     * @returns {boolean} True when the materials of the log are complete.
     */
    isValidGateMaterials(form) {
        if (!GATE_LOG_MATERIALS_FORMS.includes(form?.key))
            return true;

        return areValidPlantMaterialControlItems(this.items);
    },
    /**
     * Tells whether the fields the form is closed with are captured.
     *
     * What the requester leaves blank depends on the type of the form, so the
     * movement is only dispatched once the guard completes the fields that
     * belong to it. A form that leaves nothing to the gate is closed with the
     * notes and the evidence of the movement.
     *
     * @param {Object} [form] - Type of the form being closed.
     * @param {Object} [item] - Folio the movement belongs to, because the
     * fields of a form can depend on the direction the folio was registered with.
     * @param {Object} [blocks] - Blocks the values are asked for.
     *
     * @returns {boolean} True when every field of the form is captured.
     */
    isValidFormFields(form, item, blocks) {
        return this.getMissingFormFields(form, item, blocks).length === 0;
    },
    /**
     * Values the gate still has to write before the movement is closed.
     *
     * The guard is told which value is missing instead of being warned that
     * something is, because what the folio leaves to the gate depends on the
     * type of the form and on what the requester already registered.
     *
     * @param {Object} [form] - Type of the form being closed.
     * @param {Object} [item] - Folio the movement belongs to.
     * @param {Object} [blocks] - Blocks the values are asked for.
     *
     * @returns {Array} Names of the values that are still blank.
     */
    getMissingFormFields(form, item, blocks) {
        return getGateLogRequiredFields(form, item, blocks)
            .filter((e) => !isGateLogFieldCaptured(this[e]));
    },
    /**
     * Tells whether every material captured at the gate is complete.
     *
     * A material is added to the list only when the ones already captured have
     * their description and the movement they belong to.
     *
     * @returns {boolean} True when every material of the log is complete.
     */
    isValidItems() {
        return !this.items || this.items.filter((e) => !e.isValid()).length === 0;
    },
    /**
     * Builds the gate movement the folio is closed with.
     *
     * The movement is registered by the material control service, so it travels
     * with the folio it belongs to and the type of movement the gate wrote.
     *
     * The movement carries the moment it was registered at the gate, and not
     * the date the folio declares for it: the date of the movement is written
     * on the folio itself, so the record of the trip reads when it really
     * happened.
     *
     * @param {Object} params - Values the request is built with.
     * @param {string} params.materialControlId - Identifier of the folio.
     * @param {string} params.type - Movement the gate registered.
     * @param {Array|Object} params.files - Evidence files of the movement.
     * @param {string} params.userId - User that registers the movement.
     *
     * @returns {FormData} Body of the registration request.
     */
    toRegister({materialControlId, type, files, userId}) {
        return MATERIAL_CONTROL_GATE_EVENT_MODEL.toRegister({
            materialControlId,
            type,
            notes: this.notes,
            files,
            userId
        });
    },
    /**
     * Materials the gate writes for the folio.
     *
     * The materials are optional, so only the ones that were captured travel,
     * and each one says what it is and the movement it takes.
     *
     * @param {string} userId - User that registers the materials.
     *
     * @returns {Array} Materials the movement is registered with.
     */
    toRegisterItems(userId) {
        // The materials the folio already carries are only read at the gate, so
        // the movement is closed with the ones the guard wrote on it and none
        // of them is registered twice
        return (this.items || [])
            .filter((e) => !e?.registeredId && isNotEmpty(e?.name) && e?.type !== null)
            .map((e) => ({
                description: sanitizeString(e.name),
                type: e.type,
                userId
            }));
    },
    /**
     * Transport of the folio as the gate completes it.
     *
     * The requester can leave the transport blank, so the gate writes what the
     * vehicle arrives with; a folio that already travels with it is not
     * overwritten and nothing is sent.
     *
     * The vehicle of a crates and pallets return is written the same way, so
     * the values of it the requester left blank travel with the update under
     * the name the service reads each one with. The service stores the record
     * with what the update carries, so every value travels with it and the ones
     * the folio already registered are sent back as they were: a field left out
     * of the body is stored empty.
     *
     * @param {Object} item - Folio the movement belongs to.
     * @param {string} userId - User that registers the values.
     *
     * @returns {Object|null} Body of the update, or null when the gate wrote nothing.
     */
    toRegisterTransport(item, userId) {
        const carrierCompany = isNotEmpty(item?.carrierCompany)
            ? item.carrierCompany
            : sanitizeString(this.carrierCompany);
        const vehicleInformation = isNotEmpty(item?.vehicleInformation)
            ? item.vehicleInformation
            : sanitizeString(this.vehicleInformation);

        const wroteCarrier = !isNotEmpty(item?.carrierCompany) && carrierCompany !== null;
        const wroteVehicle = !isNotEmpty(item?.vehicleInformation) && vehicleInformation !== null;

        // The values of the vehicle a form leaves to the gate travel with the
        // record either way: the one the folio already carries is sent as it
        // was registered and only the one it left blank is written by the guard
        const vehicle = {};
        let wroteVehicleValue = false;

        GATE_VEHICLE_FIELDS.forEach((name) => {
            const registered = isNotEmpty(item?.[name]);
            const value = registered ? item[name] : sanitizeString(this[name]);

            vehicle[GATE_VEHICLE_FIELD_NAMES[name] || name] = value;

            if (!registered && value !== null)
                wroteVehicleValue = true;
        });

        if (!wroteCarrier && !wroteVehicle && !wroteVehicleValue)
            return null;

        return {
            id: item?.transport?.id,
            materialControlId: item?.id,
            carrierCompany,
            vehicleInformation,
            ...vehicle,
            userId
        };
    },
    /**
     * Values of the movement of the folio as the gate completes them: the
     * platforms and the observations.
     *
     * The values are optional for the requester, so the ones it registered
     * travel back as they were and only the ones left blank are written by the
     * guard. The update is not sent when the guard wrote nothing, so a form that
     * captures none of them never writes one.
     *
     * @param {Object} item - Folio the movement belongs to.
     * @param {string} userId - User that registers the values.
     *
     * @returns {Object|null} Body of the update, or null when the gate wrote nothing.
     */
    toRegisterMovementValues(item, userId) {
        const values = {};
        // The date of the movement is written on the folio the same way, so the
        // one the folio declares travels back and the one the guard wrote is
        // sent only when the requester left it blank
        const movementDate = this.getMovementDate();
        const dateRegistered = isNotEmpty(item?.expectedMovementDate);
        let wroteValue = !dateRegistered && movementDate !== null;

        values.expectedMovementDate = dateRegistered ? item.expectedMovementDate : movementDate;

        Object.keys(LIVE_POULTRY_TRUCK_GATE_LOG_DEFAULTS).forEach((name) => {
            const serviceName = GATE_MOVEMENT_VALUE_NAMES[name] || name;
            const registered = isNotEmpty(item?.[serviceName]);
            const value = registered ? item[serviceName] : sanitizeString(this[name]);

            values[serviceName] = value;

            if (!registered && value !== null)
                wroteValue = true;
        });

        if (!wroteValue)
            return null;

        // The service writes every value of the movement with the update, so the
        // notes the folio already carries travel back as they were
        return {id: item?.id, ...values, movementNotes: item?.movementNotes ?? null, userId};
    },
    /**
     * Thermo trailers as the gate completed them.
     *
     * The truck pulls the trailers the guard reads at the gate, so each one is
     * registered with the position it is pulled in and travels only when it
     * carries information: a trailer the folio never registered is written
     * there for the first time, and the one it already carried is sent with
     * what the gate completed on it.
     *
     * @param {Object} item - Folio the movement belongs to.
     * @param {string} userId - User that registers the values.
     *
     * @returns {Array} Trailers the gate registers, one body per position.
     */
    toRegisterTrailers(item, userId) {
        return THERMO_TRAILER_GATE_FIELDS.reduce((acc, field, index) => {
            const trailer = this[field];

            const values = Object.keys(THERMO_TRAILER_MODEL)
                .map((name) => [THERMO_TRAILER_FIELD_NAMES[name] || name, sanitizeString(trailer?.[name])])
                .filter(([, value]) => value !== null);

            if (values.length === 0)
                return acc;

            acc.push({
                materialControlId: item?.id,
                position: index + 1,
                ...Object.fromEntries(values),
                userId
            });

            return acc;
        }, []);
    },
    /**
     * Quantities of the packaging as the gate counted them.
     *
     * The quantity the requester expected travels with the folio, so only the
     * packaging that was left blank is counted at the gate: the service writes
     * one record at a time, so every count says which one it belongs to.
     *
     * @param {Object} item - Folio the movement belongs to.
     * @param {string} userId - User that registers the values.
     *
     * @returns {Array} Counts the gate registers, one body per packaging.
     */
    toRegisterPackagingQuantities(item, userId) {
        return (item?.packagingQuantities || []).reduce((acc, e) => {
            const field = PACKAGING_FIELD_BY_TYPE[e?.packagingType];
            const counted = field ? sanitizeString(this[field]) : null;

            // The quantity the folio already expects is only read at the gate,
            // so the guard writes the one that was never captured
            if (!field || e?.expectedQuantity != null)
                return acc;

            if (counted !== null)
                acc.push({
                    id: e.id,
                    materialControlId: item?.id,
                    packagingType: e.packagingType,
                    expectedQuantity: Number(counted),
                    userId
                });

            return acc;
        }, []);
    },
    /**
     * Moment the movement happened, as the guard captured it.
     *
     * The log holds the date of the direction the folio takes, so the movement
     * travels with the one that was written for it; a movement the guard closes
     * without capturing a date is registered with the moment it was sent.
     *
     * @returns {number|null} Date of the movement, or null when it carries none.
     */
    getMovementDate() {
        const date = this.entryDate ?? this.exitDate ?? null;

        return date === null ? null : new Date(date).getTime();
    }
};
